import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { fetchPlayerConfigAction } from '@pp/core/src/store/apiActions/fetchPlayerConfigAction';
import { HeaderModal } from '@pp/core/src/store/slices/header/types';

import { ChartsDataType } from '../../../api/chartsData';
import { GameConfigData } from '../../../api/gameConfigData/types';
import { LastGameDataType } from '../../../api/lastGameData/types';
import { RecentResultsItemType, RecentResultsType } from '../../../api/recentResults';
import { TableTopDataTypes } from '../../../api/tableTopData/types';
import { fetchLastGameDataAction } from '../../../store/apiActions/fetchLastGameDataAction';
import { fetchTableTopDataAction } from '../../../store/apiActions/fetchTableTopDataAction';
import { fetchChartsDataAction } from '../../apiActions/fetchChartsDataAction';
import { fetchGameConfigDataAction } from '../../apiActions/fetchGameConfigDataAction';
import { fetchRecentResultsAction } from '../../apiActions/fetchRecentResultsAction';

import { KeyPadObjectType, LeaderBoardType } from './types';

const initialSettings = {
  isSecondBetBoardToggle: true,
  isAnimationToggle: false,
  hfHideMyBets: false,
};

export type Settings = Record<string, boolean>;

interface HighflyerGameStateSlice {
  recentResultsCount: number;
  recentResults: RecentResultsType | null;
  tableStatsResult: RecentResultsType | null;
  showTimerInPlayBtn: boolean;
  chartsData: ChartsDataType | null;
  gameConfigData: GameConfigData | null;
  isTabScrollbarAtTop: boolean;
  toggleStats: boolean;
  currentCalenderOption: string;
  toggleKeypad: KeyPadObjectType;
  leaderBoardHistory: LeaderBoardType | null;
  tableTopData: Record<string, TableTopDataTypes>;
  lastGameData: LastGameDataType | null;
  currentTopTab: string;
  jackpot: boolean;
  isApiRejected: boolean;
  isLoadingTopData: boolean;
  loadingPreviousGameData: boolean;
  loadMorePreviousGameData: boolean;
  isLastResultDataFetched: boolean;
  isChatClosed: boolean;
  selectedHeader: HeaderModal | null;
  settings: Settings;
}

const initialState: HighflyerGameStateSlice = {
  recentResultsCount: 500,
  recentResults: null,
  tableStatsResult: null,
  showTimerInPlayBtn: true,
  chartsData: null,
  gameConfigData: null,
  isTabScrollbarAtTop: true,
  toggleStats: false,
  currentCalenderOption: 'day',
  tableTopData: {},
  lastGameData: null,
  leaderBoardHistory: null,
  isApiRejected: false,
  isLoadingTopData: false,
  currentTopTab: 'cashouts',
  toggleKeypad: {
    keypad: false,
    betCode: 0,
    autoCashOut: false,
  },
  jackpot: false,
  loadingPreviousGameData: true,
  loadMorePreviousGameData: true,
  isLastResultDataFetched: false,
  isChatClosed: false,
  selectedHeader: HeaderModal.Chat,
  settings: initialSettings,
};

export const highflyerGameStateSlice = createSlice({
  name: 'highflyerGameState',
  initialState,
  reducers: {
    setRecentResults: (state, action: PayloadAction<RecentResultsType>) => {
      state.recentResultsCount = action.payload.history.length;
      state.recentResults = action.payload;
    },
    setRecentResultsCount: (state, action: PayloadAction<number>) => {
      state.recentResultsCount = action.payload;
    },
    prependRecentResult: (state, action: PayloadAction<RecentResultsItemType>): void => {
      const history = state.recentResults?.history;
      const tableHistory = state.tableStatsResult?.history;
      if (history && history[0]?.gameId !== action.payload.gameId) {
        history.unshift(action.payload);
        history.length > state.recentResultsCount && history.pop();
      }
      if (tableHistory && tableHistory[0]?.gameId !== action.payload.gameId) {
        tableHistory.unshift(action.payload);
        tableHistory.length > state.recentResultsCount && tableHistory.pop();
      }
    },
    showTimerInPLayBtnStatus: (state, action: PayloadAction<boolean>) => {
      state.showTimerInPlayBtn = action.payload;
    },
    setTabScrollbarAtTop: (state, action: PayloadAction<boolean>) => {
      state.isTabScrollbarAtTop = action.payload;
    },
    setToggleStats: (state, action: PayloadAction<boolean>) => {
      state.toggleStats = action.payload;
    },
    setCurrentCalendarOption: (state, action: PayloadAction<string>) => {
      state.currentCalenderOption = action.payload;
    },
    setLeaderBoardHistory: (state, action: PayloadAction<LeaderBoardType>) => {
      state.leaderBoardHistory = action.payload;
    },
    setCurrentTopTab: (state, action: PayloadAction<string>) => {
      state.currentTopTab = action.payload;
    },
    setErrorOccured: (state) => {
      state.isApiRejected = false;
    },
    setIsLastResultError: (state) => {
      state.isLastResultDataFetched = false;
    },
    setToggleKeypad: (state, action: PayloadAction<KeyPadObjectType>) => {
      state.toggleKeypad = {
        ...action.payload,
      };
    },
    setJackpot: (state, action: PayloadAction<boolean>) => {
      state.jackpot = action.payload;
    },
    setChatClosed: (state, action: PayloadAction<boolean>) => {
      state.isChatClosed = action.payload;
    },
    setSelectedHeader: (state, action: PayloadAction<HeaderModal | null>) => {
      state.selectedHeader = action.payload;
    },
    isAnimationToggle: (state) => {
      state.settings.isAnimationToggle = !state.settings.isAnimationToggle;
    },
    isSecondBetBoardToggle: (state) => {
      state.settings.isSecondBetBoardToggle = !state.settings.isSecondBetBoardToggle;
    },
    setHfHideMyBets: (state, action: PayloadAction<boolean>) => {
      state.settings.hfHideMyBets = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRecentResultsAction.fulfilled, (state, action) => {
      if (Number(action.payload.errorCode) === 1) {
        state.isLastResultDataFetched = true;
      } else {
        state.recentResultsCount = action.payload.history.length;
        state.tableStatsResult = action.payload;
        state.recentResults = action.payload;
        state.isLastResultDataFetched = false;
      }
    });
    builder.addCase(fetchRecentResultsAction.rejected, (state) => {
      state.isLastResultDataFetched = true;
    });
    builder.addCase(fetchChartsDataAction.fulfilled, (state, action) => {
      if (Number(action.payload.errorCode) === 1) {
        state.isApiRejected = true;
      } else {
        state.chartsData = action.payload;
        state.isApiRejected = false;
      }
    });
    builder.addCase(fetchChartsDataAction.rejected, (state) => {
      state.isApiRejected = true;
    });
    builder.addCase(fetchGameConfigDataAction.fulfilled, (state, action) => {
      if (Number(action.payload.errorCode) === 1) {
        // state.isApiRejected = true;
      } else {
        state.gameConfigData = action.payload;
        // state.isApiRejected = false;
      }
    });
    builder.addCase(fetchGameConfigDataAction.rejected, (state) => {
      // state.isApiRejected = true;
    });
    builder.addCase(fetchTableTopDataAction.fulfilled, (state, action) => {
      state.isApiRejected = false;
      state.isLoadingTopData = false;
      state.tableTopData[state.currentTopTab] = action.payload;
    });
    builder.addCase(fetchTableTopDataAction.pending, (state) => {
      state.isLoadingTopData = true;
    });
    builder.addCase(fetchTableTopDataAction.rejected, (state) => {
      state.isApiRejected = true;
      state.isLoadingTopData = false;
    });
    builder.addCase(fetchLastGameDataAction.pending, (state) => {
      state.loadingPreviousGameData = false;
      state.isApiRejected = false;
    });
    builder.addCase(fetchLastGameDataAction.rejected, (state) => {
      state.isApiRejected = true;
    });
    builder.addCase(fetchLastGameDataAction.fulfilled, (state, action) => {
      state.loadingPreviousGameData = true;
      state.isApiRejected = false;
      state.lastGameData = action.payload;
      state.loadMorePreviousGameData = action.payload.players.length >= action.meta.arg.pageSize;
    });
    builder.addCase(fetchPlayerConfigAction.fulfilled, (state, action) => {
      state.settings.isAnimationToggle = action.payload.hfAnimationEnabled;
      state.settings.isSecondBetBoardToggle = action.payload.isSecondBetboardEnabled;
      state.settings.hfHideMyBets = action.payload.hfHideMyBets!;
    });
  },
});

export const highflyerGameStateActions = highflyerGameStateSlice.actions;
